import axios from "axios";
import { eventService } from "../../services/eventService";
import toast from "react-hot-toast";

/**
 * Handles article URL submission and analysis
 * @param {Event} e - The form submit event
 * @param {string} articleUrl - The article URL to analyze
 * @param {Function} setArticleError - Function to set article error
 * @param {Function} setArticleLoading - Function to set article loading state
 * @param {Function} setLoading - Function to set general loading state
 * @param {Function} setArticleContent - Function to set article content
 * @param {Function} setShowArticlePreview - Function to show article preview
 * @param {Function} enqueueSnackbar - Function to show notifications
 * @returns {Promise<string>} The analyzed article content
 */
export const handleArticleSubmit = async (
  e,
  articleUrl,
  setArticleError,
  setArticleLoading,
  setLoading,
  setArticleContent,
  setShowArticlePreview,
  enqueueSnackbar
) => {
  e.preventDefault();
  if (!articleUrl) {
    setArticleError("Please enter an article URL");
    return;
  }

  // Validate URL format
  try {
    new URL(articleUrl);
  } catch (err) {
    setArticleError("Please enter a valid URL");
    return;
  }

  setArticleLoading(true);
  setArticleError("");
  setLoading(true);

  try {
    console.log("Processing article URL:", articleUrl);

    enqueueSnackbar("Analyzing article content...", {
      variant: "info",
      autoHideDuration: 2000,
    });

    // Call our backend API to analyze the article
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/ai/analyze-article-url`,
      { url: articleUrl },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // Extract the analysis text from the response
    const analysisText = response.data.analysis;
    if (!analysisText) {
      throw new Error("Failed to extract analysis from the article");
    }

    // Set the article content without modifying the custom form data
    setArticleContent(analysisText);
    setShowArticlePreview(true);

    return analysisText;
  } catch (err) {
    console.error("Article processing error:", err);
    const errorMessage = err.message || "Failed to process article URL";
    setArticleError(errorMessage);
    throw err;
  } finally {
    setArticleLoading(false);
    setLoading(false);
  }
};

/**
 * Generates content from analyzed article
 * @param {string} articleContent - The analyzed article content
 * @param {Object} formData - The form data object
 * @param {boolean} useAdvancedSettings - Whether to use advanced settings
 * @param {boolean} includeEmojis - Whether to include emojis
 * @param {boolean} includeHashtags - Whether to include hashtags
 * @param {string} contentLength - The content length preference
 * @param {Function} setLoading - Function to set loading state
 * @param {Function} setError - Function to set error state
 * @param {Function} setGeneratedContent - Function to set generated content
 * @param {Function} setGeneratedHashtags - Function to set generated hashtags
 * @param {Function} formatResponse - Function to format response
 * @param {Object} previewRef - Ref to preview element
 * @returns {Promise<string>} The generated content
 */
export const handleGenerateFromArticle = async (
  articleContent,
  formData,
  useAdvancedSettings,
  includeEmojis,
  includeHashtags,
  contentLength,
  setLoading,
  setError,
  setGeneratedContent,
  setGeneratedHashtags,
  formatResponse,
  previewRef
) => {
  if (!articleContent) {
    toast.error("Please analyze an article first");
    return;
  }

  setLoading(true);
  setError("");

  // Create a loading toast that we can dismiss later
  const loadingToastId = toast.loading("Generating post from article...");

  try {
    // Prepare data to send to InstaImage API
    const dataToSend = {
      bio: articleContent,
      vibe: useAdvancedSettings ? formData.writingStyle : null,
      goals: useAdvancedSettings ? formData.goals : [],
      industry: useAdvancedSettings ? formData.industry : null,
      role: useAdvancedSettings ? formData.professionalRole : null,
      targetAudience: useAdvancedSettings ? formData.targetAudience : null,
      useAdvancedSettings: useAdvancedSettings,
      includeEmojis: includeEmojis,
      includeHashtags: includeHashtags,
      contentLength: contentLength,
      isFromArticle: true,
    };

    console.log("Sending article analysis to API:", dataToSend);

    // Call InstaImage API
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/ai/generate`,
      dataToSend,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // Extract content correctly
    let content = "";

    if (response.data && response.data.content) {
      content = response.data.content;
    } else if (typeof response.data === "string") {
      content = response.data;
    } else if (response.data && typeof response.data === "object") {
      const possibleContent = Object.values(response.data).find(
        (val) => typeof val === "string" && val.length > 100
      );

      if (possibleContent) {
        content = possibleContent;
      } else {
        content = JSON.stringify(response.data);
      }
    }

    // Format the content to ensure proper display
    content = formatResponse({ content });

    // Set the generated content in state
    setGeneratedContent(content);

    // Handle hashtags if they exist
    if (response.data.hashtags) {
      setGeneratedHashtags(response.data.hashtags);
    }

    // Emit event for credit tracking
    eventService.emit("CREDITS_UPDATED");

    // Dismiss the loading toast and show success
    toast.dismiss(loadingToastId);
    toast.success("Post generated from article!");

    // Scroll to preview
    setTimeout(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 500);

    return content;
  } catch (err) {
    console.error("Error generating post from article:", err);
    setError("Failed to generate post from article. Please try again.");

    // Dismiss the loading toast and show error
    toast.dismiss(loadingToastId);
    toast.error("Error generating post from article");
    throw err;
  } finally {
    setLoading(false);
  }
};
